import { CreateWorkDetailReq, DeleteWorkDetailReq, UpdateWorkDetailBulkInfoFileReq, UpdateWorkDetailBulkInfoReq, UpdateWorkDetailBulkReq, UpdateWorkDetailReq, UpdateReorderListReq } from '../../dto/ReqDtoRepository';
import { WorkDetailListResponse, WorkDetailResponse, WorkDetailSimpleListResponse } from '../../dto/ResDtoRepository';
import { deleteWorkDetail, getSimpleWorkDetailListForOwner, getWorkDetail, getWorkDetailList, reorderWorkDetail, toggleDetailAllowDownload, updateWorkDetailBulk, updateWorkDetailBulkInfo } from '../../api/workDetailApi';
import { useWorkViewStore } from '../../store/WorkViewStore';
import { useImportStationWorkStore } from '../../store/importStationWorkStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { normalizeWorkDetailSimpleData } from '../../util/normalizeWorkData';
import { toUpdateWorkDetailBulkInfoReq } from '../../util/workDetailBulkInfo';
import { useApiWrapper } from './apiWrapper';
import { useImage } from './useImage';
import { useModalStore } from '../../store/portal/modalStore';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { ServiceType } from '../../enum/EnumRepository';

interface UseWorkDetailActionsResult {
  getWorkDetail: (workDetailId: string) => Promise<void>;
  getSimpleWorkDetailList: (workId: string) => Promise<void>;
  getWorkDetailList: (workId: string) => Promise<void>;
  updateWorkDetailBulk: (data: UpdateWorkDetailBulkReq) => Promise<void>;
  updateWorkDetailBulkInfo: (data: UpdateWorkDetailBulkInfoReq) => Promise<void>;
  updateChangedWorkDetailBulkInfo: () => Promise<void>;
  toggleDetailAllowDownload: (data: UpdateWorkDetailReq) => Promise<void>;
  deleteWorkDetail: (data: DeleteWorkDetailReq) => Promise<void>;
  reorderWorkDetail: (data: UpdateReorderListReq) => Promise<void>;
}

export const useWorkDetailActions = (): UseWorkDetailActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setActiveWorkDetailList = useWorkViewStore((s) => s.setActiveWorkDetailList);
  const setOnlyActiveWorkDetailList = useWorkViewStore((s) => s.setOnlyActiveWorkDetailList);
  const afterDeleteActiveWorkDetail = useWorkViewStore((s) => s.afterDeleteActiveWorkDetail);
  const afterUpdateWorkDetailBulkInfo = useWorkViewStore((s) => s.afterUpdateWorkDetailBulkInfo);
  const setDetailListForWork = useImportStationWorkStore((s) => s.setDetailListForWork);
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((s) => s.setDeletedTempAlert);
  const clearModal = useModalStore((s) => s.clearModal);
  const withApiHandler = useApiWrapper();
  const { uploadImage } = useImage();

  const handleGetWorkDetailSuccess = (_response: WorkDetailResponse) => {
  };

  const handleGetWorkDetailListSuccess = (response: WorkDetailListResponse) => {
    setActiveWorkDetailList(response.data);
  };

  const handleGetSimpleWorkDetailListSuccess = (response: WorkDetailSimpleListResponse, workId: string) => {
    const data = response.data.map(normalizeWorkDetailSimpleData);
    setDetailListForWork(workId, data);
  };

  const handleToggleDetailAllowDownloadSuccess = (response: WorkDetailResponse) => {
    const data = response.data;
    const { activeWorkDetailList } = useWorkViewStore.getState();
    setOnlyActiveWorkDetailList(
      activeWorkDetailList.map((wd) => (wd.id === data.id ? data : wd)),
    );
    setUpdatedTempAlert();
  };

  const handleDeleteWorkDetailSuccess = (workDetailId: string) => {
    afterDeleteActiveWorkDetail(workDetailId);
    console.log("deleted well");
    setDeletedTempAlert();
  };

  const handleReorderWorkDetailSuccess = (response: WorkDetailListResponse) => {
    setActiveWorkDetailList(response.data);
    setUpdatedTempAlert();
  };

  const handleUpdateWorkDetailBulkSuccess = (response: WorkDetailListResponse) => {
    const data = response.data;
    const { activeWorkDetailList } = useWorkViewStore.getState();
    setUpdatedTempAlert();
    setOnlyActiveWorkDetailList([...activeWorkDetailList, ...data]);
    clearModal();
  };

  const handleUpdateWorkDetailBulkInfoSuccess = (response: WorkDetailListResponse) => {
    afterUpdateWorkDetailBulkInfo(response.data);
    setUpdatedTempAlert();
  };

  const handleWorkDetailRequest = async (
    action: 'get' | 'get list' | 'get simple list' | 'update_bulk' | 'update_bulk_info' | 'delete' | 'toggle allowDownload' | 'reorder',
    data: UpdateWorkDetailReq | CreateWorkDetailReq | DeleteWorkDetailReq | UpdateWorkDetailBulkReq | UpdateWorkDetailBulkInfoReq | UpdateReorderListReq | string
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete': {
          const workDetailId =
            (data as DeleteWorkDetailReq).workDetailId;
          await runCurrentArchiveRequest(
            aui,
            () => deleteWorkDetail(aui, data as DeleteWorkDetailReq),
            () => handleDeleteWorkDetailSuccess(workDetailId),
          );
          break;
        }
        case 'update_bulk':
          await runCurrentArchiveRequest(
            aui,
            () => updateWorkDetailBulk(aui, data as UpdateWorkDetailBulkReq),
            handleUpdateWorkDetailBulkSuccess,
          );
          break;
        case 'update_bulk_info':
          await runCurrentArchiveRequest(
            aui,
            () => updateWorkDetailBulkInfo(aui, data as UpdateWorkDetailBulkInfoReq),
            handleUpdateWorkDetailBulkInfoSuccess,
          );
          break;
        case 'toggle allowDownload':
          await runCurrentArchiveRequest(
            aui,
            () => toggleDetailAllowDownload(aui, data as UpdateWorkDetailReq),
            handleToggleDetailAllowDownloadSuccess,
          );
          break;
        case 'reorder':
          await runCurrentArchiveRequest(
            aui,
            () => reorderWorkDetail(aui, data as UpdateReorderListReq),
            handleReorderWorkDetailSuccess,
          );
          break;
        case 'get list':
          await runCurrentArchiveRequest(
            aui,
            () => getWorkDetailList(aui, data as string),
            handleGetWorkDetailListSuccess,
          );
          break;
        case 'get simple list': {
          const workId = data as string;
          await runCurrentArchiveRequest(
            aui,
            () => getSimpleWorkDetailListForOwner(aui, workId),
            response => handleGetSimpleWorkDetailListSuccess(response, workId),
          );
          break;
        }
        case 'get':
        default:
          await runCurrentArchiveRequest(
            aui,
            () => getWorkDetail(aui, data as string),
            handleGetWorkDetailSuccess,
          );
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getWorkDetailHandler = (data: string) => handleWorkDetailRequest('get', data);
  const getSimpleWorkDetailListHandler = (workId: string) => handleWorkDetailRequest('get simple list', workId);
  const getWorkDetailListHandler = (workId: string) => handleWorkDetailRequest('get list', workId);
  const updateWorkDetailBulkHandler = (data: UpdateWorkDetailBulkReq) => handleWorkDetailRequest('update_bulk', data);
  const updateWorkDetailBulkInfoHandler = (data: UpdateWorkDetailBulkInfoReq) =>
    handleWorkDetailRequest('update_bulk_info', data);
  const updateChangedWorkDetailBulkInfoHandler = async () => {
    const { activeWork, activeWorkDetailList } = useWorkViewStore.getState();
    if (!activeWork) return;
    const changed = activeWorkDetailList.filter((detail) => detail.hasChanged);
    if (changed.length === 0) return;

    const uploadedFileById: Record<string, UpdateWorkDetailBulkInfoFileReq> = {};
    for (const detail of changed) {
      if (!detail.imageChanged) continue;
      const uploaded = await uploadImage(ServiceType.DETAIL, {
        id: detail.id,
        workId: activeWork.id,
        description: detail.description,
        detailType: detail.detailType,
        videoUrl: detail.videoUrl ?? '',
        updateUploadFileReq: {
          originUrl: detail.uploadFile.originUrl,
          uploadFileId: detail.uploadFile.id,
          allowDownload: detail.uploadFile.allowDownload,
          width: detail.uploadFile.width,
          height: detail.uploadFile.height,
        },
      });
      if (!uploaded?.updateUploadFileReq) return;
      uploadedFileById[detail.id] = {
        uploadFileId: uploaded.updateUploadFileReq.uploadFileId,
        originUrl: uploaded.updateUploadFileReq.originUrl,
        width: uploaded.updateUploadFileReq.width,
        height: uploaded.updateUploadFileReq.height,
      };
    }

    const request = toUpdateWorkDetailBulkInfoReq(activeWork.id, activeWorkDetailList, uploadedFileById);
    if (!request) return;
    return handleWorkDetailRequest('update_bulk_info', request);
  };
  const toggleDetailAllowDownloadHandler = (data: UpdateWorkDetailReq) => handleWorkDetailRequest('toggle allowDownload', data);
  const deleteWorkDetailHandler = (data: DeleteWorkDetailReq) => handleWorkDetailRequest('delete', data);
  const reorderWorkDetailHandler = (data: UpdateReorderListReq) => handleWorkDetailRequest('reorder', data);

  return {
    getWorkDetail: getWorkDetailHandler,
    getSimpleWorkDetailList: getSimpleWorkDetailListHandler,
    getWorkDetailList: getWorkDetailListHandler,
    updateWorkDetailBulk: updateWorkDetailBulkHandler,
    updateWorkDetailBulkInfo: updateWorkDetailBulkInfoHandler,
    updateChangedWorkDetailBulkInfo: updateChangedWorkDetailBulkInfoHandler,
    toggleDetailAllowDownload: toggleDetailAllowDownloadHandler,
    deleteWorkDetail: deleteWorkDetailHandler,
    reorderWorkDetail: reorderWorkDetailHandler,
  };
};
